import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles, type Article } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article: Article | undefined = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-4">文章不存在</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.categoryId === article.categoryId && a.id !== article.id)
    .slice(0, 3);

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.cardImageUrl,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishDate,
    publisher: {
      '@type': 'Organization',
      name: '火影西瓜',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://huoyingxigua.com/article/${article.slug}`,
    },
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <article className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: article.categoryName, href: `/category/${article.categorySlug}` },
              { label: article.title },
            ]}
          />

          <header className="mb-8">
            <span className="inline-block px-3 py-1 text-xs bg-accent-100 text-accent-700 rounded-md mb-4">
              {article.categoryName}
            </span>
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-user-line text-xs"></i>
                </span>
                {article.author}
              </span>
              <span>{article.publishDate}</span>
              <span>{article.readTime} 分钟阅读</span>
              <span className="flex items-center gap-1">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-eye-line text-xs"></i>
                </span>
                {article.views.toLocaleString()} 阅读
              </span>
            </div>
          </header>

          <div className="mb-10 rounded-lg overflow-hidden">
            <img
              src={article.detailImageUrl}
              alt={article.title}
              title={article.title}
              className="w-full h-auto object-cover"
            />
          </div>

          <div
            className="prose-custom mb-12"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="flex flex-wrap gap-2 mb-10">
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 bg-secondary-100 text-secondary-700 rounded-full text-xs font-medium"
              >
                #{tag}
              </span>
            ))}
          </div>

          <div className="border-t border-background-200/70 pt-10">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading text-lg font-bold text-foreground-900">相关文章</h3>
              <Link
                to={`/category/${article.categorySlug}`}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap cursor-pointer"
              >
                查看更多 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {relatedArticles.map((related) => (
                <Link
                  key={related.id}
                  to={`/article/${related.slug}`}
                  className="group block bg-background-100 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all cursor-pointer"
                >
                  <div className="relative w-full h-36 overflow-hidden">
                    <img
                      src={related.cardImageUrl}
                      alt={related.title}
                      title={related.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-3">
                    <h4 className="font-heading text-sm font-bold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {related.title}
                    </h4>
                    <p className="text-xs text-foreground-400 mt-1">{related.publishDate}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </article>

        <div className="max-w-4xl mx-auto px-4 md:px-6 py-14">
          <div className="bg-background-100 rounded-lg p-6 md:p-8 text-center">
            <h3 className="font-heading text-lg font-bold text-foreground-900 mb-2">
              觉得这篇文章有用？
            </h3>
            <p className="text-sm text-foreground-500 mb-4">
              分享给更多火影迷，一起解锁忍者风吃瓜新姿势！
            </p>
            <div className="flex items-center justify-center gap-3">
              <button className="w-10 h-10 rounded-full bg-background-200 flex items-center justify-center text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer">
                <i className="ri-wechat-line text-lg"></i>
              </button>
              <button className="w-10 h-10 rounded-full bg-background-200 flex items-center justify-center text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer">
                <i className="ri-weibo-line text-lg"></i>
              </button>
              <button className="w-10 h-10 rounded-full bg-background-200 flex items-center justify-center text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer">
                <i className="ri-share-line text-lg"></i>
              </button>
            </div>
          </div>
        </div>
      </main>
      <Footer />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
    </>
  );
}